import React, { useState } from 'react';
import { Check } from 'lucide-react';

/**
 * Renders GeeVee's `ask_followup_choice` options as tappable controls under the
 * AI bubble — radio-style for single-select (tap submits immediately) or
 * checkbox-style for multi-select (tap several, then Send). The chosen label(s)
 * become the agent's next message via `onSubmit`.
 *
 * Render this ONLY for the latest message so old questions don't leave stale
 * buttons in the transcript.
 */
const FollowupChoices = ({ followup, onSubmit, disabled = false }) => {
  const multi = !!(followup && followup.multi_select);
  const [selected, setSelected] = useState([]);

  if (!followup || !Array.isArray(followup.options) || followup.options.length === 0) {
    return null;
  }

  const toggle = (opt) => {
    if (disabled) return;
    if (!multi) {
      onSubmit(opt);
      return;
    }
    setSelected((prev) => (prev.includes(opt) ? prev.filter((o) => o !== opt) : [...prev, opt]));
  };

  const submitMulti = () => {
    if (disabled || selected.length === 0) return;
    onSubmit(selected.join(', '));
  };

  return (
    <div className="mt-2 flex flex-col gap-1.5">
      {followup.options.map((opt) => {
        const isSel = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            disabled={disabled}
            className={`flex items-center gap-2 text-left text-sm px-3.5 py-2 rounded-xl border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isSel
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-200 bg-white hover:border-blue-400 hover:bg-blue-50/50 text-gray-800'
            }`}
          >
            <span
              className={`flex items-center justify-center w-4 h-4 flex-shrink-0 border ${
                multi ? 'rounded' : 'rounded-full'
              } ${isSel ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}
            >
              {isSel && <Check size={11} className="text-white" />}
            </span>
            <span>{opt}</span>
          </button>
        );
      })}
      {multi && (
        <button
          type="button"
          onClick={submitMulti}
          disabled={disabled || selected.length === 0}
          className="self-start mt-0.5 text-sm px-4 py-1.5 rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {selected.length ? `Send (${selected.length})` : 'Send'}
        </button>
      )}
    </div>
  );
};

export default FollowupChoices;
